import {
  FormControl,
  FormHelperText,
  FormLabel,
  Typography,
  Box,
  Tooltip,
} from '@material-ui/core';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  section: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  mainLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    fontSize: '0.875rem',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
}));

const LAMBDA_LAYERS = [
  {
    label: 'Datadog',
    value: 'datadog',
  },
  {
    label: 'Secrets',
    value: 'secrets',
  },
];

interface LambdaLayersSelectorProps {
  param_and_secrets_ext_enabled?: boolean;
  datadog_lambda_apm_enabled?: boolean;
  onChange: (updates: {
    param_and_secrets_ext_enabled?: boolean;
    datadog_lambda_apm_enabled?: boolean;
  }) => void;
  label?: string;
  helperText?: string;
  /** Disable the Secrets layer toggle (e.g. LMI lambdas don't support the secrets layer). */
  secretsDisabled?: boolean;
  /** Tooltip shown over the Secrets layer control when disabled. */
  secretsDisabledReason?: string;
}

export const LambdaLayersSelector = ({
  param_and_secrets_ext_enabled = false,
  datadog_lambda_apm_enabled = false,
  onChange,
  label = 'Lambda Layers',
  helperText,
  secretsDisabled = false,
  secretsDisabledReason,
}: LambdaLayersSelectorProps) => {
  const classes = useStyles();

  const handleToggle = (layerValue: string) => {
    if (layerValue === 'secrets') {
      if (secretsDisabled) return;
      onChange({ param_and_secrets_ext_enabled: !param_and_secrets_ext_enabled });
    } else if (layerValue === 'datadog') {
      onChange({ datadog_lambda_apm_enabled: !datadog_lambda_apm_enabled });
    }
  };

  return (
    <Box className={classes.section}>
      <FormControl fullWidth>
        <FormLabel component="legend" className={classes.mainLabel}>
          {label}
        </FormLabel>

        <div
          className={classes.optionsContainer}
          style={{ display: 'flex', flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}
        >
          {LAMBDA_LAYERS.map(layer => {
            let isChecked = false;
            const isDisabled = layer.value === 'secrets' && secretsDisabled;
            if (layer.value === 'secrets') {
              isChecked = secretsDisabled ? false : param_and_secrets_ext_enabled;
            } else if (layer.value === 'datadog') {
              isChecked = datadog_lambda_apm_enabled;
            }

            const control = (
              <FormControlLabel
                key={layer.value}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleToggle(layer.value)}
                    color="primary"
                    size="small"
                    disabled={isDisabled}
                  />
                }
                label={
                  <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                    {layer.label}
                  </Typography>
                }
              />
            );

            return isDisabled && secretsDisabledReason ? (
              <Tooltip key={layer.value} title={secretsDisabledReason}>
                <span>{control}</span>
              </Tooltip>
            ) : (
              control
            );
          })}
        </div>

        {helperText && <FormHelperText style={{ marginTop: 8 }}>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
